import { useState, useSyncExternalStore, type CSSProperties, type ReactElement } from 'react';
import { cheminEcran, ecransAccessibles, libelleNavigation } from '../../app/ecrans.js';
import { Lien } from '../../app/navigation.js';
import { COULEURS } from '../../app/styles.js';

/** Largeur en dessous de laquelle le portail se présente comme une application (barre d'onglets en bas). */
export const LARGEUR_MOBILE = 768;

const abonner = (rappel: () => void): (() => void) => {
  window.addEventListener('resize', rappel);
  return () => window.removeEventListener('resize', rappel);
};

/** Vrai sur téléphone. Lu sur la fenêtre : les écrans du registre ne reçoivent pas la largeur de la coquille. */
export function useEstMobile(): boolean {
  return useSyncExternalStore(
    abonner,
    () => window.innerWidth < LARGEUR_MOBILE,
    () => false,
  );
}

export type OngletPortail = 'M04' | 'X14' | 'M05' | 'M06' | 'M07';

const ONGLETS: readonly { id: OngletPortail; libelle: string }[] = [
  { id: 'M04', libelle: 'Accueil' },
  { id: 'X14', libelle: 'Mon dossier' },
  { id: 'M05', libelle: 'Envoyer une pièce' },
  { id: 'M06', libelle: 'Régler ma part' },
  { id: 'M07', libelle: 'Mes coordonnées' },
];

const styleBarre: CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 20,
  // Colonnes égales, réductibles sous la largeur d'un mot : un libellé long passe à la ligne au lieu de déborder.
  display: 'grid',
  gridTemplateColumns: 'repeat(5, minmax(0, 1fr)) auto',
  alignItems: 'stretch',
  background: COULEURS.surface,
  borderTop: `1px solid ${COULEURS.bordure}`,
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
};

const styleOnglet = (courant: boolean): CSSProperties => ({
  minWidth: 0,
  minHeight: 56,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: '4px 4px',
  fontSize: 13,
  lineHeight: 1.2,
  overflowWrap: 'anywhere',
  hyphens: 'auto',
  textDecoration: 'none',
  fontWeight: courant ? 700 : 500,
  color: courant ? COULEURS.marine : COULEURS.ardoise,
  borderTop: `3px solid ${courant ? COULEURS.marine : 'transparent'}`,
});

const styleBoutonPlus: CSSProperties = { ...styleOnglet(false), font: 'inherit', fontSize: 14, background: 'transparent', border: 0, cursor: 'pointer', minWidth: 56 };

const stylePanneau: CSSProperties = {
  position: 'fixed',
  right: 8,
  bottom: 64,
  zIndex: 21,
  display: 'flex',
  flexDirection: 'column',
  background: COULEURS.surface,
  border: `1px solid ${COULEURS.bordure}`,
  borderRadius: 8,
  padding: 4,
};

function ecransDuMenuPlus(): { id: string; libelle: string; chemin: string }[] {
  const dejaEnBarre = new Set<string>(ONGLETS.map((o) => o.id));
  return ecransAccessibles('heritier')
    .filter((e) => !dejaEnBarre.has(e.id) && e.id !== 'X13')
    .map((e) => ({ id: e.id, libelle: libelleNavigation(e.id), chemin: cheminEcran(e.id) }));
}

/**
 * Barre d'onglets fixée en bas du portail héritier sur téléphone : cinq entrées vers les écrans existants, le reste
 * dans un menu « Plus » replié. `piece` est la pièce attendue (M05 s'ouvre dessus) ; `mobile` force l'affichage (tests).
 */
export function BarreOngletsPortail({ actif, piece, mobile }: { actif: string; piece: string | null; mobile?: boolean }): ReactElement | null {
  const detecte = useEstMobile();
  const [ouvert, setOuvert] = useState(false);
  if (!(mobile ?? detecte)) return null;
  const reste = ecransDuMenuPlus();
  return (
    <nav aria-label="Navigation du portail" style={styleBarre}>
      {ONGLETS.map((o) => {
        const chemin = cheminEcran(o.id);
        return (
          <Lien
            key={o.id}
            vers={o.id === 'M05' && piece ? `${chemin}?piece=${encodeURIComponent(piece)}` : chemin}
            nav={o.id}
            courant={o.id === actif}
            style={styleOnglet(o.id === actif)}
            enfants={o.libelle}
          />
        );
      })}
      {reste.length > 0 ? (
        <>
          <button type="button" aria-expanded={ouvert} aria-controls="menu-plus-portail" onClick={() => setOuvert((v) => !v)} style={styleBoutonPlus}>
            Plus
          </button>
          {ouvert ? (
            <div id="menu-plus-portail" style={stylePanneau}>
              {reste.map((e) => (
                <Lien key={e.id} vers={e.chemin} nav={e.id} courant={e.id === actif} style={{ ...styleOnglet(e.id === actif), justifyContent: 'flex-start', padding: '4px 16px' }} enfants={e.libelle} />
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </nav>
  );
}
